import colorPallete, {
    bodyFonts,
    buttonFonts,
    themeFontWeights,
} from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { Button, Clickable } from "@insite/mobius/index";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { FC, useState } from "react";
import { css } from "styled-components";

const enum fields {
    content = "content",
    enableShowLessMore = "enableShowLessMore",
    showLessLabelText = "showLessLabelText",
    showMoreLabelText = "showMoreLabelText",
    variantType = "variantType",
}

const enum variants {
    primaryLink = "primaryLink",
    secondaryLink = "secondaryLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.content]: string;
        [fields.enableShowLessMore]: boolean;
        [fields.showLessLabelText]: string;
        [fields.showMoreLabelText]: string;
        [fields.variantType]: variants;
        
    };
}

export interface ShowLessMoreStyles {
    wrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    linkBtn?: InjectableCss;
}

export const showLessMoreStyles: ShowLessMoreStyles = {
    wrapper: {
        css: css`
            margin: 8px 0;
            ul, 
            ol {
                margin-top: 0;
            }
            .ellipsisText {
                align-items: center;
                display: flex;
                line-height: 20px;
                margin: 8px 0 0;
                top: -8px;
                position: relative;
            }
        `
    },
    contentWrapper: {
        css: css`
            p,
            ul,
            li {
                font: ${bodyFonts.body2RegularSm.fontWeight} ${bodyFonts.body2RegularSm.fontSize} /
                    ${bodyFonts.body2RegularSm.lineHeight} ${bodyFonts.body2RegularSm.fontFamily};
                color: ${colorPallete.grey800};
            }
            p {
                margin-bottom: 16px;
            }
            ul {
                ul {
                    padding-left: 22px;
                }
            }
            > p {
                &:last-child {
                    margin-bottom: 0;
                }
            }
            strong {
                font-weight: ${themeFontWeights.semiBold};
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            &.textFail {
                                -webkit-line-clamp: 5 !important;
                            }
                            &.textPass {
                                -webkit-line-clamp: initial !important;
                            }
                        `,
                    ],
                    "min",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            &.textFail {
                                max-height: calc(5 * 24px) !important;
                                &.allPTags {
                                    max-height: calc(5 * 21px) !important;
                                }
                            }
                            &.textPass {
                                max-height: initial !important;
                                margin-bottom: 16px;
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },

    linkBtn: {
        css: css`
            &, 
            span {
                cursor: pointer;
                font: ${buttonFonts.buttonSm.fontWeight} ${buttonFonts.buttonSm.fontSize} /
                    ${buttonFonts.buttonSm.lineHeight} ${buttonFonts.buttonSm.fontFamily};
                text-decoration: underline;
                color: initial;
            }
            &[class*="ButtonWrapper"],
            &[class*="StyledButton"] {
                min-height: initial;
                padding: 0;
                margin-top: 0;
            }
            &,
            &:hover,
            &:focus {
                background: transparent;
                border: none;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            &[class*="ButtonWrapper"],
                            &[class*="StyledButton"] {
                                margin: 8px 0;
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
};

const styles = showLessMoreStyles;

export const ShowLessMore: FC<OwnProps> = ({ fields }) => {

    const [expanded, setExpanded] = useState(false);

    const containerStyle = {
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        "-webkit-box-orient": "vertical",
    };

   
    function areAllTagsP(htmlString: string) {
        if (typeof document !== 'undefined') {
            const div = document.createElement('div');
            div.innerHTML = htmlString;
            const children = div.children;
        
            for (let i = 0; i < children.length; i++) {
                if (children[i].tagName.toLowerCase() !== 'p') {
                    return false; 
                }
            }
            return true;  
        }
    }


    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper
                {...styles.contentWrapper}
                id="bottomTextWrapper"
                style={containerStyle}
                className={`${fields.enableShowLessMore ? 
                    `${expanded ? "textPass" : "textFail"} ${areAllTagsP(fields.content) ? 'allPTags' : 'notAllPTags'}` 
                    : ''}`}
            >
                {parse(fields.content, parserOptions)}
            </StyledWrapper>
            {(!expanded && fields.enableShowLessMore) && <Typography className="ellipsisText mobileSpecific">...</Typography>}
            
            {(fields.content && fields.enableShowLessMore &&
                fields.content.split("\n").length >= 5) && (
                    <Clickable {...styles.linkBtn} onClick={() => setExpanded(!expanded)}
                    className={`
                        ${fields.variantType === variants.primaryLink ? 'text-link-primary' : ''}
                        ${fields.variantType === variants.secondaryLink ? 'text-link-secondary' : ''}
                    `}>
                        {expanded
                            ? fields.showLessLabelText
                            : fields.showMoreLabelText}
                    </Clickable>
                )
            }
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ShowLessMore,
    definition: {
        group: "Common",
        displayName: "Show Less More",
        fieldDefinitions: [
            {
                name: fields.content,
                editorTemplate: "RichTextField",
                displayName: "Content",
                defaultValue: `
                    <h4 style="margin:0;">Lead Time Slashed from 60 Weeks to 9 Weeks</h4>
                    <p>In 2023, an end user ethanol facility ordered an electrical switchboard and motor control to power and control a corn dryer -- with a hard deadline for harvest time. The lead time started out at 40 weeks, then was pushed to 60 weeks due to supply chain challenges after the order was placed. </p>
                    <p>Because of the delays, the ethanol facility worked together with us to reverse engineer the specified design, which included using our UL508 panel shop, DSG Engineered solutions.</p>
                    <p>DSG Engineered is not only where we build custom control cabinets, but we also assemble the Schneider Electric product, FlexSeT.  FlexSeT enables us to build switchboards up to 2000a today, and soon up to 2500/3000a. We also have the ability to provide this solution in a Nema 1 (indoor) or Nema 3R (outdoor) configuration.</p>
                    <p>Within DSG Engineered, we were able to provide control cabinets, along with the FlexSeT offering to satisfy the original Motor Control Center offering that was specified.</p>
                    <p>DSG was able to meet the facility’s seasonal deadlines with a complete, in-house solution, and we were able to have this solution on-site within 9 weeks. This not only brought the project back on track, but also helped them reach their goal (and deadline) for harvest.</p>
                    <p>This is a great testament to our dedication and partnership with you, as well as our commitment as employee owners to our communities. We are proud to be part of our communities, and will continue to focus on delivering exceptional products, services, and solutions.</p>
                `,
                extendedConfig: { height: 170 },
                fieldType: "Contextual",
            },
            {
                name: fields.enableShowLessMore,
                displayName: "Enable Show Less More Feature",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showLessLabelText,
                displayName: "Show Less Label Text",
                editorTemplate: "TextField",
                defaultValue: "View less",
                fieldType: "General",
                isVisible: widget => widget.fields.enableShowLessMore,
            },
            {
                name: fields.showMoreLabelText,
                displayName: "Show More Label Text",
                editorTemplate: "TextField",
                defaultValue: "View more",
                fieldType: "General",
                isVisible: widget => widget.fields.enableShowLessMore,
            },
            {
                name: fields.variantType,
                displayName: "Link Variant Type",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: variants.primaryLink },
                    { displayName: "Secondary", value: variants.secondaryLink },
                ],
                fieldType: "General",
                defaultValue: variants.secondaryLink,
                isVisible: widget => widget.fields.enableShowLessMore,
            },
        ],
    },
};

export default widgetModule;
